import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import { buildVariantForest, loadVariantTree } from './variant-tree';
import { selectProjectVariant } from './select-project-variant';

export function VariantTreeDialog({ open, projectGuid, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const current = useSelector((state) => state.variant.current);
  const controllerRef = useRef(null);
  const [state, setState] = useState({ entries: [], loading: true, listError: false });
  const [selectionError, setSelectionError] = useState(false);
  const [selectingGuid, setSelectingGuid] = useState(null);
  const forest = useMemo(() => buildVariantForest(state.entries), [state.entries]);

  useEffect(() => {
    if (!open || !projectGuid) {
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ entries: [], loading: true, listError: false });
    setSelectionError(false);
    setSelectingGuid(null);
    loadVariantTree({
      projectGuid,
      signal: controller.signal,
      onUpdate: (newState) => {
        if (!controller.signal.aborted) {
          setState(newState);
        }
      },
    }).catch((error) => {
      if (!controller.signal.aborted) {
        setState((previousState) => ({ ...previousState, loading: false, listError: true }));
      }
    });
    return () => {
      controller.abort();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    };
  }, [open, projectGuid]);

  const handleClose = () => {
    controllerRef.current?.abort();
    onClose();
  };

  const selectVariant = async (variant) => {
    const controller = controllerRef.current;
    if (!controller || controller.signal.aborted || selectingGuid !== null) {
      return;
    }
    setSelectionError(false);
    setSelectingGuid(variant.calculationGuid);
    try {
      await selectProjectVariant({ variant, dispatch, signal: controller.signal });
    } catch (error) {
      if (!controller.signal.aborted) {
        setSelectionError(true);
      }
    } finally {
      if (!controller.signal.aborted) {
        setSelectingGuid(null);
      }
    }
  };

  const hasMetadataWarnings = state.entries.some((entry) => entry.provenance?.warning);
  const hasRelationWarnings = [...forest.known, ...forest.unknown].some((row) => row.relationIssue);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" aria-labelledby="variant-tree-title">
      <DialogTitle id="variant-tree-title">{t('variantTree.title')}</DialogTitle>
      <DialogContent dividers>
        {state.loading && <LinearProgress aria-label={t('variantTree.loading')} />}
        {state.loading && <Typography role="status">{t('variantTree.loading')}</Typography>}
        {state.listError && <Alert severity="error">{t('variantTree.listError')}</Alert>}
        {(hasMetadataWarnings || hasRelationWarnings) && (
          <Alert severity="warning">{t('variantTree.metadataWarning')}</Alert>
        )}
        {selectionError && <Alert severity="error">{t('variantTree.selectionError')}</Alert>}
        {!state.loading && !state.listError && state.entries.length === 0 && (
          <Typography>{t('variantTree.empty')}</Typography>
        )}
        <List component="div" disablePadding>
          {['known', 'unknown'].flatMap((group) => forest[group].length > 0 ? [
            <Typography key={`group:${group}`} component="h3" variant="subtitle1" sx={{ mt: 2 }}>
              {t(group === 'known' ? 'variantTree.knownRoots' : 'variantTree.unknownRoots')}
            </Typography>,
            ...forest[group].map(({ entry, depth, relationIssue }) => {
              const { variant, provenance } = entry;
              const selected = current?.projectGuid === projectGuid &&
                current?.calculationGuid === variant.calculationGuid;
              return (
                <ListItemButton
                  key={`variant:${variant.calculationGuid}`}
                  selected={selected}
                  aria-current={selected ? 'true' : undefined}
                  disabled={selectingGuid !== null}
                  onClick={() => selectVariant(variant)}
                  sx={{ ml: depth * 2, borderLeft: depth > 0 ? '1px solid' : undefined, borderColor: 'divider' }}
                >
                  <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <Typography>
                      {variant.calculationName || variant.calculationGuid}
                      {selected && ` — ${t('variantTree.current')}`}
                    </Typography>
                    {provenance === undefined ? (
                      <Typography variant="caption">{t('variantTree.metadataLoading')}</Typography>
                    ) : (
                      <Typography variant="caption">
                        {relationIssue ? t('variantTree.relationUnavailable') :
                          provenance.source?.kind === 'glb' ? t('variantTree.glbRoot') :
                            depth === 0 ? t('variantTree.unknownRelation') : null}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              );
            }),
          ] : [])}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={handleClose}>{t('variantTree.close')}</Button></DialogActions>
    </Dialog>
  );
}
